<script lang="ts" setup>
import type { Components } from '@workbench/core';
import { ref, watch, reactive } from 'vue';
import {
  useResponsiveImage,
  mkBBDResponsiveParams,
} from '@dh-io-owpi/shared-dynamic-stage/src/composables/useResponsiveImage';
import { STAGE_INITIAL_PERSPECTIVE as initialPerspective } from '@dh-io-owpi/shared-dynamic-stage/src/utils/constants';

import { useTrackingStore } from '@dh-io-owpi/shared/src/stores/tracking';
import type { StageVehicle } from '@dh-io-owpi/backend-api/src/data-contracts';
import { useAemData } from '@dh-io-owpi/shared/src/plugins/aemData';
import { mkBBDImgUrl } from '@dh-io-owpi/shared-dynamic-stage/src/utils/bbdImgUrls';
import { cacheKill, preloadImage, waitForLoad } from '@dh-io-owpi/shared/src/utils/img.util';
import { vStencilReady } from '@dh-io-owpi/shared/src/directives/stencil-ready.directive';
import { useLoading } from '@dh-io-owpi/shared/src/composables/useLoading';

type WB360 = HTMLElement & Components.Wb360Viewer;
type ImageConfig = Components.Wb360Viewer['images'][0];

const aemData = useAemData();

const props = defineProps<{
  stageVehicle?: StageVehicle;
  darkMode?: boolean;
  hasErrors?: boolean;
}>();

const wbv = ref<WB360 | undefined>();
const setWbv = (el: HTMLElement) => (wbv.value = el as WB360);

const loadState = reactive({
  low: false,
  high: false,
});

const ri = useResponsiveImage();

const rotationTracked = ref(false);
const { track } = useTrackingStore();

const trackRotation = ({ detail }: CustomEvent<number>) => {
  if (rotationTracked.value || detail === initialPerspective / 10) return;
  track({ action: 'dynamic_stage', label: 'stage_360', category: 'feature' });
  rotationTracked.value = true;
};

async function preloadHighResImages(e: MouseEvent) {
  if (loadState.high) return;

  const images = (e.currentTarget as WB360).images;
  const r = await Promise.allSettled(
    images.map((image) =>
      waitForLoad(image.highRes.img!, image.highRes.src) //
        .then(() => (image.highRes.isLoaded = true)),
    ),
  );
  cacheKill(r, images, 'highRes');
  loadState.high = true;
}

const { isLoading, load } = useLoading('stage');

function setImages(wb360: WB360, images: ImageConfig[]) {
  loadState.low = false;
  loadState.high = false;
  wb360.images = images;
  // wait for all low res images to be loaded
  Promise.allSettled(images.map((image) => waitForLoad(image.lowRes!.img!))) //
    .then((r) => {
      cacheKill(r, images, 'lowRes');
      loadState.low = true;
    });
}

watch([wbv, ri, () => props.stageVehicle], ([wb360, responsiveWidth, stageVehicle], [_wbv, _ri, oldStageVehicle]) => {
  if (stageVehicle && wb360) {
    const baseImageUrl = stageVehicle.baseImageUrl;
    const images = Array.from<unknown, ImageConfig>({ length: 36 }, (_, i) => ({
      highRes: { src: mkBBDImgUrl(baseImageUrl, i * 10, responsiveWidth) },
      lowRes: { src: mkBBDImgUrl(baseImageUrl, i * 10, 240) },
    }));

    if (stageVehicle !== oldStageVehicle) {
      load(
        preloadImage(images[wb360.currentIndex].highRes.src).then(() => {
          /**
           * Apply the images to the 360 viewer
           * But first set a transition to give a smooth image change
           * and reset it after, so it does not look bad when rotating
           */
          const img = wb360.querySelector('.wb-360-viewer__image') as HTMLDivElement;

          if (img) {
            img.style.transition = 'cubic-bezier(0, 0, 0.3, 1) background 0.2s';
            setTimeout(() => (img.style.transition = ''), 200);
          }

          setImages(wb360, images);
        }),
      );
    } else {
      setImages(wb360, images);

      // preload the first high res image
      load(
        waitForLoad(images[initialPerspective / 10].highRes.img!).finally(() => {
          // remove the ssr placeholder image after the first image is loaded
          placeholderImage.value = undefined;
        }),
      );
    }
  }
});

// SSR placeholder image
const placeholderImage = ref(
  aemData.renderMode !== 'csr' &&
    props.stageVehicle &&
    mkBBDResponsiveParams(props.stageVehicle.baseImageUrl, initialPerspective),
);
</script>
<template>
  <div class="owpi-threesixty-viewer" :aria-disabled="isLoading || !loadState.low">
    <wb-360-viewer
      v-stencil-ready="setWbv"
      show-hint
      legacy-assets="false"
      :loading="isLoading || !loadState.low"
      :theme="darkMode ? 'dark' : 'light'"
      :current-index="initialPerspective / 10"
      @wbchange="trackRotation"
      @touchstart="preloadHighResImages"
      @mouseover="preloadHighResImages"
    />
    <img v-if="placeholderImage" v-bind="placeholderImage" alt="Placeholder image while car is loading" />
  </div>
</template>
<style scoped lang="scss">
.owpi-threesixty-viewer {
  position: relative;
  aspect-ratio: 16 / 9;

  &[aria-disabled='true'] {
    wb-360-viewer {
      pointer-events: none;
    }
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      max-width: var(--wb-grid-width);
      margin-inline: auto;
      cursor: not-allowed;
    }
  }

  // SSR placeholder image
  > img {
    position: absolute;
    inset: 0;
    object-fit: contain;
    width: 100%;
    aspect-ratio: 16 / 9;
  }

  :deep(wb-360-viewer) {
    &:focus {
      outline: none;
    }
    &:focus-visible::after {
      content: '';
      position: absolute;
      inset: 10% 0;
      border: 1px solid var(--wb-blue-45);
    }

    .wb-360-viewer__overlay {
      pointer-events: none;
    }
  }

  @include breakpoint-to(mq2) {
    :deep(wb-360-viewer) .wb-360-viewer__image,
    > img {
      // we're scalling the stage content to fit better the content grid
      transform: scale(1.25);
    }
    :deep(wb-360-viewer) .wb-360-hint {
      top: 55% !important;
    }
  }
  @include breakpoint-between(mq3, mq4) {
    :deep(wb-360-viewer) .wb-360-hint {
      top: 50% !important;
    }
  }
}
</style>
